import React, { createContext, useContext, useEffect, useState } from "react";

import apiService from "../services/apiService";

// Crear el Context
const UserContext = createContext();

// Hook personalizado para usar el Context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

// Provider del Context
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userEvents, setUserEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Efecto para cargar el usuario desde localStorage al iniciar
  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        // Primero intentar cargar del nuevo sistema
        const userData = apiService.getCurrentUser();
        if (userData) {
          setUser(userData);
        } else {
          // Fallback al sistema legacy
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            const legacyUserData = JSON.parse(storedUser);
            setUser(legacyUserData);
          }
        }
      } catch (error) {
        console.error("Error loading user from localStorage:", error);
        localStorage.removeItem("user");
        apiService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  // Función para login con API
  const loginWithApi = async (email, password) => {
    try {
      const userData = await apiService.login({ email, password });
      setUser(userData);

      // También guardar en el sistema legacy para compatibilidad
      localStorage.setItem("user", JSON.stringify(userData));

      // Cargar eventos del usuario (por ahora mock, después del backend)
      await loadUserEvents(userData.id);

      return userData;
    } catch (error) {
      throw error;
    }
  };

  // Función para registro con API
  const registerWithApi = async (userData) => {
    try {
      const newUser = await apiService.register(userData);
      setUser(newUser);

      // También guardar en el sistema legacy para compatibilidad
      localStorage.setItem("user", JSON.stringify(newUser));

      // Inicializar eventos vacíos para nuevo usuario
      setUserEvents([]);

      return newUser;
    } catch (error) {
      throw error;
    }
  };

  // Función para login con Google
  const googleLogin = async (credential, userData) => {
    try {
      const user = await apiService.googleLogin({ credential, userData });
      setUser(user);

      // También guardar en el sistema legacy para compatibilidad
      localStorage.setItem("user", JSON.stringify(user));

      // Cargar eventos del usuario
      await loadUserEvents(user.id);

      return user;
    } catch (error) {
      throw error;
    }
  };

  // Función para cargar eventos del usuario
  const loadUserEvents = async (userId) => {
    if (!userId) return;

    setEventsLoading(true);
    try {
      // Primero ejecutar limpieza de eventos expirados
      try {
        const cleanupResult = await apiService.cleanupExpiredEvents();
        if (cleanupResult.deleted_events > 0) {
          console.log(
            `Cleaned up ${cleanupResult.deleted_events} expired events and ${cleanupResult.deleted_photos} photos`
          );
        }
      } catch (cleanupError) {
        console.warn("Failed to cleanup expired events:", cleanupError);
        // No bloqueamos la carga de eventos si falla la limpieza
      }

      // Usar el API service real - ahora con IDs numéricos simples
      const events = await apiService.getEvents(userId);
      setUserEvents(events);
    } catch (error) {
      console.error("Error loading user events:", error);
      // En caso de error, podemos mostrar un array vacío o mantener los eventos anteriores
      setUserEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Función para crear evento
  const createEvent = async (eventData) => {
    if (!user) throw new Error("Usuario no autenticado");

    try {
      // Mapear los datos del formulario al formato esperado por la API
      const apiEventData = {
        name: eventData.eventName,
        description: eventData.description,
        date: eventData.eventDate,
        time: eventData.eventTime,
        location: eventData.location,
      };

      // Usar el API service real en lugar del mock
      const newEvent = await apiService.createEvent(apiEventData, user.id);

      // Agregar el evento a la lista local
      setUserEvents((prev) => [newEvent, ...prev]);

      // Actualizar estadísticas del usuario
      setUser((prev) => ({
        ...prev,
      }));

      return newEvent;
    } catch (error) {
      throw error;
    }
  };

  // Función para eliminar evento
  const deleteEvent = async (eventId) => {
    if (!user) throw new Error("Usuario no autenticado");

    try {
      await apiService.deleteEvent(eventId, user.id);
      setUserEvents((prev) => prev.filter((event) => event.id !== eventId));

      // Actualizar estadísticas del usuario
      setUser((prev) => ({
        ...prev,
      }));

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Función para actualizar evento
  const updateEvent = async (eventData) => {
    if (!user) throw new Error("Usuario no autenticado");
    try {
      const updated = await apiService.updateEvent(eventData, user.id);
      setUserEvents((prev) =>
        prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e))
      );
      return updated;
    } catch (error) {
      throw error;
    }
  };

  // Cargar eventos cuando el usuario cambie
  useEffect(() => {
    if (user && user.id) {
      loadUserEvents(user.id);
    } else {
      setUserEvents([]);
    }
  }, [user?.id]);

  // Función para logout
  const logout = () => {
    setUser(null);
    setUserEvents([]);
    localStorage.removeItem("user");
    apiService.logout();
    window.location.href = "/";
  };

  // Función para actualizar datos del usuario
  const updateUser = (updatedData) => {
    const newUserData = { ...user, ...updatedData };
    setUser(newUserData);
    localStorage.setItem("user", JSON.stringify(newUserData));
  };

  // Verificar si el usuario está autenticado
  const isAuthenticated = () => {
    return user !== null && apiService.isAuthenticated();
  };

  // Nueva función para actualizar estado premium
  const refreshPremiumStatus = async () => {
    try {
      if (!user) {
        return null;
      }

      const response = await apiService.getPremiumStatus(user.id);

      if (response.success) {
        // Update user with latest premium status and expiration info
        const updatedUser = {
          ...user,
          isPremium: response.user.isPremium,
          eventLimit: response.user.eventLimit,
          premiumStartDate: response.user.premiumStartDate,
          premiumEndDate: response.user.premiumEndDate,
          premiumExpired: response.user.premiumExpired,
          daysUntilExpiration: response.user.daysUntilExpiration,
          features: response.user.features,
          eventCount: response.user.eventCount,
        };

        setUser(updatedUser);

        // Update localStorage
        localStorage.setItem("user", JSON.stringify(updatedUser));

        return response.user;
      } else {
        throw new Error(response.message || "Failed to get premium status");
      }
    } catch (error) {
      console.error("Error refreshing premium status:", error);
      return null;
    }
  };

  const upgradeToPremium = async (paymentMethod = "manual") => {
    try {
      if (!user) {
        throw new Error("User not logged in");
      }

      const response = await apiService.upgradeToPremium(
        user.id,
        paymentMethod
      );

      if (response.success) {
        // Refresh premium status to get the latest data
        await refreshPremiumStatus();
        return response;
      } else {
        throw new Error(response.message || "Failed to upgrade to premium");
      }
    } catch (error) {
      console.error("Error upgrading to premium:", error);
      throw error;
    }
  };

  const downgradeFromPremium = async () => {
    try {
      if (!user) {
        throw new Error("User not logged in");
      }

      const response = await apiService.downgradeFromPremium(user.id);

      if (response.success) {
        // Refresh premium status to get the latest data
        await refreshPremiumStatus();
        return response;
      } else {
        throw new Error(response.message || "Failed to downgrade from premium");
      }
    } catch (error) {
      console.error("Error downgrading from premium:", error);
      throw error;
    }
  };

  // Función para verificar si el premium está próximo a expirar
  const isPremiumExpiringSoon = (daysThreshold = 7) => {
    if (!user || !user.isPremium || !user.daysUntilExpiration) {
      return false;
    }
    return user.daysUntilExpiration <= daysThreshold;
  };

  // Auto-refresh premium status cuando el usuario inicia sesión
  useEffect(() => {
    if (user && user.id && user.isPremium) {
      refreshPremiumStatus();
    }
  }, [user?.id]); // Solo ejecutar cuando cambie el ID del usuario

  // Valor del contexto
  const value = {
    user,
    setUser,
    isLoading,
    userEvents,
    eventsLoading,
    login: loginWithApi,
    googleLogin,
    register: registerWithApi,
    logout,
    updateUser,
    createEvent,
    deleteEvent,
    updateEvent,
    loadUserEvents,
    isAuthenticated: isAuthenticated(),
    upgradeToPremium,
    downgradeFromPremium,
    refreshPremiumStatus,
    isPremiumExpiringSoon,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
